import {buildLabelData} from '@/modules/preparation-label/build-label-document'
import {getLabelCaptions} from '@/modules/preparation-label/label-assets'
import {getCompletionLog} from '@/modules/preparation-label/map-label-source'
import {describePrintError} from '@/modules/preparation-label/format'
import {PreparationLabelPdf} from '@/modules/preparation-label/preparation-label-pdf'
import {useLabelTranslate} from '@/modules/preparation-label/use-label-translate'
import {useGetSettings} from '@/modules/settings/api'
import {queries} from '@/shared/integrations/zero'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {openPdfBlob} from '@/shared/utils/open-pdf'
import type {LabelPrintPlace} from '@cypro/engine/mutators'
import type {CompoundingResult} from '@cypro/engine/utils'
import {toaster} from '@cypro/ui/toaster'
import {pdf} from '@react-pdf/renderer'
import {useQuery} from '@rocicorp/zero/react'
import {useTranslate} from '@tolgee/react'
import {useState} from 'react'

/** `place` is where the label is printed from; it goes into the Label printed log. */
export function usePrintPreparationLabel(
  sequenceId: string,
  targets: CompoundingResult | null,
  place: LabelPrintPlace,
  labelReady = false
) {
  const {t} = useTranslate('cleanroom')
  const zero = useZero()
  const {settings} = useGetSettings()
  const [source] = useQuery(queries.patientsTherapies.getPreparationLabelSource(sequenceId))
  const [statusLogs] = useQuery(queries.patientsTherapies.getSequenceStatusLogs(sequenceId))
  const [isPrinting, setIsPrinting] = useState(false)
  const getLabelTranslate = useLabelTranslate()

  const hasCompletionLog = getCompletionLog(statusLogs).producedAt !== null
  // printable once the source is loaded and production completed (this session or the synced log)
  const canPrint = Boolean(source && settings) && (labelReady || hasCompletionLog)
  const disabledReason = canPrint || !source || !settings ? undefined : t('label_not_available_yet')

  async function print() {
    if (!source || !settings) return
    setIsPrinting(true)
    try {
      const translate = await getLabelTranslate()
      const {label, assets} = await buildLabelData({source, statusLogs, settings, targets, translate})

      const blob = await pdf(
        <PreparationLabelPdf label={label} {...assets} captions={getLabelCaptions(key => translate(key))} />
      ).toBlob()

      openPdfBlob(blob, t('preparation_label.popup_blocked'))
      zero.attemptMutation(zero.mutators.patientsTherapies.logLabelPrint({sequenceId, place}))
    } catch (error) {
      toaster.error({
        title: t('preparation_label.print_failed'),
        description: describePrintError(error, t),
      })
    } finally {
      setIsPrinting(false)
    }
  }

  return {print, isPrinting, canPrint, disabledReason, hasCompletionLog}
}
